import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const STATE_CONFIG = {
  OPEN: {
    bg: 'bg-emerald-600',
    header: 'STUDIO OPEN',
    subtext: null
  },
  RECORDING: {
    bg: 'bg-rose-600 animate-breathe',
    header: 'RECORDING',
    subtext: 'Do Not Disturb'
  },
  LEADIN: {
    bg: 'bg-amber-500',
    header: 'STANDBY',
    subtext: 'Next recording starts soon'
  },
  WRAPUP: {
    bg: 'bg-slate-800',
    header: 'WRAPPING UP',
    subtext: 'Available shortly'
  },
  BREAK: {
    bg: 'bg-violet-600',
    header: 'ON BREAK',
    subtext: 'Stepped away momentarily'
  },
  CLOSED: {
    bg: 'bg-zinc-950',
    header: 'STUDIO CLOSED',
    subtext: null
  }
};

const getTzDay = (date, tz) => {
  try {
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return tzDate.getDay();
  } catch (e) {
    return date.getDay();
  }
};

const getLocalTimeStr = (date, tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', timeZone: tz, hourCycle: 'h23'
    }).formatToParts(date);
    const h = parts.find(p => p.type === 'hour')?.value || '00';
    const m = parts.find(p => p.type === 'minute')?.value || '00';
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  } catch (e) {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
};

function MainDisplayContent() {
  const [searchParams] = useSearchParams();
  const studioId = searchParams.get('s');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeStr, setTimeStr] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [forceSyncTick, setForceSyncTick] = useState(Date.now());

  useEffect(() => {
    document.title = "StudioSign | Main Display";
  }, []);

  // HW Throttling Recovery & Wake Lock
  useEffect(() => {
    let wakeLock = null;
    let lastSync = Date.now();

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.error(`Wake Lock error: ${err.name}, ${err.message}`);
      }
    };

    requestWakeLock();

    const triggerWakeUpSync = () => {
      const now = Date.now();
      if (now - lastSync > 1000) {
        lastSync = now;
        setCurrentTime(new Date());
        setForceSyncTick(now);
      }
    };

    const handleVisibilityChange = () => {
      if (wakeLock !== null && document.visibilityState === 'visible') requestWakeLock();
      if (document.visibilityState === 'visible') triggerWakeUpSync();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', triggerWakeUpSync);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', triggerWakeUpSync);
      if (wakeLock) wakeLock.release().catch(console.error);
    };
  }, []);

  // Real-Time Clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Initial Fetch & Real-time Subscription
  useEffect(() => {
    if (!studioId) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('studio_settings')
        .select('id, active_door_state, dynamic_time_target, target_time_ms, studio_name, show_name, show_clock, operating_days, timezone, open_time, close_time, lead_in_buffer, wrap_up_buffer, is_calendar_connected, tracked_keywords')
        .eq('id', studioId)
        .single();

      if (data && !error) setSettings(data);
      setLoading(false);
    };

    fetchSettings();

    const channel = supabase
      .channel(`studio_settings_${studioId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'studio_settings', filter: `id=eq.${studioId}` },
        (payload) => { setSettings((prev) => ({ ...prev, ...payload.new })); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [studioId]);

  // ── Calendar polling loop → get-calendar-events Edge Function ─────────────
  useEffect(() => {
    if (!settings?.is_calendar_connected || !studioId) {
      setEvents([]);
      return;
    }

    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-calendar-events', {
          body: { studio_id: studioId }
        });

        if (error) {
          console.warn('[Display] Calendar Edge Function error. Falling back to base operating hours.', error.message);
          setEvents([]);
          return;
        }

        const rawEvents = data?.events || [];

        let rawKeywords = settings.tracked_keywords;
        let keywords = Array.isArray(rawKeywords)
          ? rawKeywords
          : (typeof rawKeywords === 'string' ? rawKeywords.split(',') : []);
        keywords = keywords.map(k => String(k).toLowerCase().trim()).filter(Boolean);

        const filteredEvents = rawEvents.filter(item => {
          if (item.status === 'cancelled') return false;
          if (!item.start?.dateTime || !item.end?.dateTime) return false;
          if (keywords.length === 0) return true;
          return keywords.some(keyword => String(item.summary || '').toLowerCase().includes(keyword));
        });

        setEvents(filteredEvents.map(e => ({
          start: e.start.dateTime,
          end: e.end.dateTime,
          summary: e.summary
        })));

      } catch (e) {
        console.error('[Display] Calendar fetch exception:', e);
      }
    };

    fetchEvents();
    const interval = setInterval(fetchEvents, 30000);
    return () => clearInterval(interval);
  }, [settings?.is_calendar_connected, settings?.tracked_keywords, studioId, forceSyncTick]);

  // Auto Brain
  const autoComputed = useMemo(() => {
    if (!settings) return { state: 'CLOSED', targetTime: null };
    try {
      const tz = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const currentDayInt = getTzDay(currentTime, tz);
      const activeDays = (settings.operating_days || [1, 2, 3, 4, 5]).map(Number);
      let baseState = 'CLOSED';

      if (activeDays.includes(currentDayInt)) {
        const currentTimeStr = getLocalTimeStr(currentTime, tz);
        const openTime = settings.open_time || '09:00';
        const closeTime = settings.close_time || '17:00';
        const isOperatingHours = openTime > closeTime
          ? (currentTimeStr >= openTime || currentTimeStr < closeTime)
          : (currentTimeStr >= openTime && currentTimeStr < closeTime);
        if (isOperatingHours) baseState = 'OPEN';
      }

      const currentMs = currentTime.getTime();
      const parsedLeadIn = parseInt(settings.lead_in_buffer, 10);
      const leadInMs = (isNaN(parsedLeadIn) ? 15 : parsedLeadIn) * 60000;
      const parsedWrapUp = parseInt(settings.wrap_up_buffer, 10);
      const wrapUpMs = (isNaN(parsedWrapUp) ? 5 : parsedWrapUp) * 60000;

      let calendarState = null;
      let targetTime = null;

      for (const event of events) {
        if (!event.start || !event.end) continue;
        try {
          const startMs = new Date(event.start).getTime();
          const endMs = new Date(event.end).getTime();
          if (isNaN(startMs) || isNaN(endMs)) continue;

          if (currentMs >= startMs && currentMs < endMs) {
            calendarState = 'RECORDING'; targetTime = null; break;
          } else if (currentMs >= (startMs - leadInMs) && currentMs < startMs) {
            if (calendarState !== 'RECORDING') {
              calendarState = 'LEADIN';
              targetTime = new Date(startMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
            }
          } else if (currentMs >= endMs && currentMs < (endMs + wrapUpMs)) {
            if (calendarState !== 'RECORDING' && calendarState !== 'LEADIN') {
              calendarState = 'WRAPUP';
              targetTime = new Date(endMs + wrapUpMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
            }
          }
        } catch (err) { continue; }
      }

      return calendarState ? { state: calendarState, targetTime } : { state: baseState, targetTime: null };
    } catch (e) {
      console.error('Auto Brain Error:', e);
      return { state: 'CLOSED', targetTime: null };
    }
  }, [settings, currentTime, events]);

  // Switchboard & Priority Logic
  let manualState = settings?.active_door_state?.toUpperCase() || 'AUTO';
  if (settings?.target_time_ms && currentTime.getTime() >= settings.target_time_ms) {
    manualState = 'AUTO';
  }
  const isManualOverride = manualState !== 'AUTO' && manualState !== 'NONE';

  const switchboardState = isManualOverride ? manualState : autoComputed.state;
  const switchboardTargetTime = isManualOverride ? (settings?.dynamic_time_target || null) : autoComputed.targetTime;

  const config = STATE_CONFIG[switchboardState] || STATE_CONFIG['CLOSED'];
  let displaySubtext = config.subtext;
  if (switchboardTargetTime) {
    if (switchboardState === 'BREAK') displaySubtext = `Be back at ${switchboardTargetTime}`;
    if (switchboardState === 'LEADIN') displaySubtext = `Next recording starts at ${switchboardTargetTime}`;
    if (switchboardState === 'WRAPUP') displaySubtext = `Available at ${switchboardTargetTime}`;
  }

  // Live Clock
  useEffect(() => {
    const updateTime = () => {
      const tz = settings?.timezone;
      const now = new Date();
      try {
        const timePart = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...(tz ? { timeZone: tz } : {}) });
        const datePart = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(tz ? { timeZone: tz } : {}) }).toUpperCase();
        setTimeStr(`${datePart} • ${timePart}`);
      } catch (e) {
        const timePart = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const datePart = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
        setTimeStr(`${datePart} • ${timePart}`);
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [settings?.timezone]);

  const handleDoubleTap = async () => {
    if (!document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen(); }
      catch (err) { console.error('Fullscreen request:', err); }
    }
  };

  if (!studioId || loading || !settings) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center transition-colors duration-1000 ease-in-out">
        <span className="text-zinc-800 text-sm font-mono">Awaiting Studio ID...</span>
      </div>
    );
  }

  return (
    <div
      className={`w-screen h-screen overflow-hidden flex flex-col relative transition-colors duration-1000 ease-in-out cursor-default ${config.bg}`}
      onDoubleClick={handleDoubleTap}
    >
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <h1 className="font-extrabold tracking-tighter text-white text-[14vmax] leading-none mb-2 transition-opacity duration-700 ease-in-out">
          {config.header}
        </h1>
        <div className="h-[4vmax] flex items-start justify-center">
          <p className={`font-semibold text-white drop-shadow-md text-[3vmax] leading-none transition-all duration-700 ease-in-out transform ${
            displaySubtext ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}>
            {displaySubtext}
          </p>
        </div>
      </div>

      <div className="absolute bottom-8 w-full px-8 md:px-12 flex justify-between items-end gap-6">
        <div className="flex-1 flex justify-start">
          {settings.show_name && settings.studio_name?.trim() && (
            <div className="px-6 py-3 bg-black/20 backdrop-blur-md rounded-2xl border border-white/10 text-white/90 flex items-center">
              <div className="font-semibold tracking-widest uppercase text-[1.2vmax] max-w-[40vw] text-left leading-snug break-words line-clamp-2 transition-opacity duration-700 ease-in-out">
                {settings.studio_name}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 flex justify-end">
          {settings.show_clock && (
            <div className="px-6 py-3 bg-black/20 backdrop-blur-md rounded-2xl border border-white/10 text-white/90 flex items-center">
              <div className="font-semibold tracking-widest uppercase text-[1.2vmax] whitespace-nowrap transition-opacity duration-700 ease-in-out">
                {timeStr}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MainDisplay() {
  return (
    <Suspense fallback={
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center">
        <span className="text-zinc-800 text-sm font-mono">Awaiting Studio ID...</span>
      </div>
    }>
      <MainDisplayContent />
    </Suspense>
  );
}